import React, { useContext, useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Transition } from 'react-transition-group';
import Modal from '../common/Modal/Modal';
import Input from '../common/Input/Input';
import { Context } from '../../main';
import { useNotification } from '../../hooks/useNotification';
import { fetchFunction } from '../../utils/function';

const PASSWORD_REG = /^(?=.{8})(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)[a-zA-Z\d]+$/;
const PASSWORD_HINT = 'Пароль має містити принаймні одну цифру, одну малу та велику латинські літери і містити мінімум 8 символів';
// Запарковано разом із ротацією паролів на бекенді (back/utils/constants.js):
// const PASSWORD_MAX_AGE_DAYS = 30; // синхронно з PASSWORD_MAX_AGE_DAYS бекенду

// Примусова зміна тимчасового пароля після першого входу.
// (Друга причина — протермінований пароль — запаркована разом із ротацією,
// див. back/utils/constants.js.)
// Рендериться з Home.jsx ЗАМІСТЬ app-контенту, коли
// store.isAuth && store.user.password_change_required.
// Єдиний спосіб закрити без зміни пароля — вийти з системи.
const ForcedPasswordChangeModalComponent = () => {
    const { store } = useContext(Context);
    const notification = useNotification();
    const nodeRef = useRef(null);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [profile, setProfile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // profileSchema на PUT /api/users/profile вимагає email/first_name/last_name —
    // підвантажуємо профіль одразу, щоб submit був одним запитом
    useEffect(() => {
        const loadProfile = async () => {
            try {
                const response = await fetchFunction('api/users/profile');
                setProfile(response.data?.[0] ?? null);
            } catch (error) {
                notification({
                    type: 'warning',
                    title: 'Помилка',
                    message: error?.response?.data?.message ?? 'Не вдалося завантажити профіль',
                    placement: 'top'
                });
            }
        };
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validationError = () => {
        if (!PASSWORD_REG.test(password)) return PASSWORD_HINT;
        if (password !== confirm) return 'Паролі не збігаються';
        return null;
    };

    const handleSubmit = async () => {
        const error = validationError();
        if (error) {
            notification({ type: 'warning', title: 'Помилка', message: error, placement: 'top' });
            return;
        }
        if (!profile) {
            notification({ type: 'warning', title: 'Помилка', message: 'Профіль ще завантажується, спробуйте за мить', placement: 'top' });
            return;
        }
        try {
            setSubmitting(true);
            await fetchFunction('api/users/profile', {
                method: 'put',
                data: {
                    email: profile.email,
                    first_name: profile.first_name,
                    last_name: profile.last_name,
                    middle_name: profile.middle_name ?? '',
                    phone: profile.phone ?? '',
                    password,
                }
            });
            store.clearPasswordChangeRequired();
            notification({
                type: 'success',
                title: 'Успіх',
                message: 'Пароль змінено — новий пароль надіслано вам на пошту.',
                placement: 'top',
                duration: 3
            });
        } catch (error) {
            notification({
                type: 'warning',
                title: 'Помилка',
                message: error?.response?.data?.message ?? 'Не вдалося змінити пароль',
                placement: 'top'
            });
            if (error?.response?.status === 401) store.logOff();
        } finally {
            setSubmitting(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <Transition in={true} appear timeout={200} nodeRef={nodeRef}>
            {state => (
                <Modal
                    className={state === 'entered' ? 'modal-window-wrapper--active' : ''}
                    onClose={() => store.logout()}
                    onOk={handleSubmit}
                    title="Зміна тимчасового пароля"
                    okText={submitting ? 'Збереження...' : 'Змінити пароль'}
                    cancelText="Вийти"
                    confirmLoading={submitting}
                    okDisabled={!password || !confirm}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <p className="paragraph">
                            Ви увійшли з тимчасовим паролем. Для продовження роботи
                            встановіть власний пароль.
                        </p>
                        <p className="paragraph" style={{ fontSize: '13px', color: '#7b8794' }}>
                            Новий пароль продублюємо вам на пошту.
                        </p>
                        <Input
                            type="password"
                            name="new_password"
                            value={password}
                            onChange={(_n, value) => setPassword(value)}
                            onKeyDown={onKeyDown}
                            placeholder="Новий пароль"
                            autoComplete="new-password"
                        />
                        <Input
                            type="password"
                            name="confirm_password"
                            value={confirm}
                            onChange={(_n, value) => setConfirm(value)}
                            onKeyDown={onKeyDown}
                            placeholder="Повторіть новий пароль"
                            autoComplete="new-password"
                        />
                        <p className="paragraph" style={{ fontSize: '13px', color: '#7b8794' }}>
                            {PASSWORD_HINT}
                        </p>
                    </div>
                </Modal>
            )}
        </Transition>
    );
};

const ForcedPasswordChangeModal = observer(ForcedPasswordChangeModalComponent);
export default ForcedPasswordChangeModal;
